import React, { useState } from 'react'
import { X, Link as LinkIcon, AlertCircle, Loader, Calendar, Image, Video, FileText, User, Sparkles } from 'lucide-react'
import api from '../../services/api'

interface CreateURLResourceModalProps {
  onClose: () => void
  onSuccess: () => void
}

const URL_TYPES = [
  { value: 'calendar', label: 'Calendario', icon: Calendar, color: 'text-blue-600' },
  { value: 'image', label: 'Imagen', icon: Image, color: 'text-green-600' },
  { value: 'video', label: 'Video', icon: Video, color: 'text-red-600' },
  { value: 'document', label: 'Documento', icon: FileText, color: 'text-yellow-600' },
  { value: 'profile', label: 'Perfil', icon: User, color: 'text-purple-600' },
  { value: 'other', label: 'Otro', icon: Sparkles, color: 'text-gray-600' },
]

// Ejemplos predefinidos
const EXAMPLES = {
  calendar: {
    name: 'Calendario de Reuniones',
    url: 'https://cal.com/usuario/30min',
    usage_context: 'Usar cuando el usuario quiera agendar una reunión, consulta, llamada o cita',
    usage_keywords: ['agendar', 'reunión', 'cita', 'llamada', 'calendario', 'meeting'],
    response_template: 'Puedes agendar una reunión de 30 minutos en mi calendario: {url}'
  },
  image: {
    name: 'Imagen del Producto X',
    url: 'https://cdn.example.com/producto-x.jpg',
    usage_context: 'Usar cuando el usuario pregunte sobre el Producto X o solicite ver imágenes del producto',
    usage_keywords: ['producto x', 'imagen', 'foto', 'ver producto', 'cómo se ve'],
    response_template: 'Aquí está la imagen del Producto X: {url}'
  },
  video: {
    name: 'Tutorial de Setup',
    url: 'https://youtube.com/watch?v=example',
    usage_context: 'Usar cuando el usuario necesite ayuda con el setup inicial o configuración',
    usage_keywords: ['setup', 'configurar', 'instalar', 'tutorial', 'cómo empezar', 'primeros pasos'],
    response_template: 'Te recomiendo este video tutorial que explica el proceso paso a paso: {url}'
  },
  document: {
    name: 'Documentación Técnica',
    url: 'https://docs.example.com/technical-guide.pdf',
    usage_context: 'Usar cuando el usuario necesite documentación técnica o detalles de implementación',
    usage_keywords: ['documentación', 'docs', 'técnico', 'api', 'referencia', 'manual'],
    response_template: 'Puedes consultar la documentación técnica completa aquí: {url}'
  },
  profile: {
    name: 'Perfil Profesional',
    url: 'https://linkedin.com/in/usuario',
    usage_context: 'Usar cuando pregunten sobre experiencia laboral, CV o perfil profesional',
    usage_keywords: ['experiencia', 'cv', 'linkedin', 'perfil', 'trabajo', 'carrera'],
    response_template: 'Puedes ver mi perfil profesional completo en LinkedIn: {url}'
  }
}

const CreateURLResourceModal: React.FC<CreateURLResourceModalProps> = ({ onClose, onSuccess }) => {
  const [url, setUrl] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [urlType, setUrlType] = useState('other')
  const [usageContext, setUsageContext] = useState('')
  const [usageKeywords, setUsageKeywords] = useState('')
  const [responseTemplate, setResponseTemplate] = useState('Aquí está el enlace que buscabas: {url}')
  const [tags, setTags] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadExample = (type: string) => {
    const example = EXAMPLES[type as keyof typeof EXAMPLES]
    if (example) {
      setName(example.name)
      setUrl(example.url)
      setUsageContext(example.usage_context)
      setUsageKeywords(example.usage_keywords.join(', '))
      setResponseTemplate(example.response_template)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!url.trim() || !name.trim() || !usageContext.trim() || !responseTemplate.trim()) {
      setError('URL, nombre, contexto de uso y template son requeridos')
      return
    }

    // Validate URL format
    try {
      new URL(url)
    } catch {
      setError('URL inválida. Debe incluir protocolo (https://)')
      return
    }

    // Validate response template
    if (!responseTemplate.includes('{url}')) {
      setError('El template debe incluir {url} como marcador de posición')
      return
    }

    try {
      setLoading(true)

      const keywordsArray = usageKeywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0)

      const tagsArray = tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0)

      await api.urlResources.create({
        url: url.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        usage_context: usageContext.trim(),
        usage_keywords: keywordsArray.length > 0 ? keywordsArray : undefined,
        response_template: responseTemplate.trim(),
        url_type: urlType,
        tags: tagsArray.length > 0 ? tagsArray : undefined
      })

      onSuccess()
    } catch (err: any) {
      console.error('Error creating URL resource:', err)
      setError(err.response?.data?.error || 'Error al crear el recurso URL')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <LinkIcon className="text-blue-600" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Crear Recurso URL</h2>
              <p className="text-sm text-gray-600 mt-1">Enlaces inteligentes con contexto de uso</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Tipo de Recurso *
            </label>
            <div className="grid grid-cols-3 gap-3">
              {URL_TYPES.map((type) => {
                const Icon = type.icon
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => {
                      setUrlType(type.value)
                      loadExample(type.value)
                    }}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                      urlType === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={type.color} size={24} />
                    <span className="text-sm font-medium text-gray-900">{type.label}</span>
                  </button>
                )
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Selecciona un tipo para cargar un ejemplo
            </p>
          </div>

          {/* URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL *
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del Recurso *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Calendario de Reuniones"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción (opcional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descripción del recurso"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Usage Context */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contexto de Uso * <span className="text-gray-500 font-normal">- ¿Cuándo usar este recurso?</span>
            </label>
            <textarea
              value={usageContext}
              onChange={(e) => setUsageContext(e.target.value)}
              placeholder="Ej: Usar cuando el usuario quiera agendar una reunión o consulta"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Palabras Clave <span className="text-gray-500 font-normal">- Separadas por comas</span>
            </label>
            <input
              type="text"
              value={usageKeywords}
              onChange={(e) => setUsageKeywords(e.target.value)}
              placeholder="Ej: agendar, reunión, cita, llamada, calendario"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Palabras que activarán este recurso cuando el usuario las mencione
            </p>
          </div>

          {/* Response Template */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template de Respuesta * <span className="text-gray-500 font-normal">- Usa {'{url}'} como marcador</span>
            </label>
            <textarea
              value={responseTemplate}
              onChange={(e) => setResponseTemplate(e.target.value)}
              placeholder="Ej: Puedes agendar una reunión en mi calendario: {url}"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              El sistema reemplazará {'{url}'} con la URL real al generar la respuesta
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (opcional) <span className="text-gray-500 font-normal">- Separados por comas</span>
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Ej: calendario, personal, contacto"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Búsqueda Semántica Inteligente</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• El sistema usará embeddings para encontrar este recurso cuando sea relevante</li>
              <li>• Las keywords boost la precisión del matching</li>
              <li>• El template permite personalizar cómo se presenta el enlace</li>
              <li>• Cada uso se registra para estadísticas y mejoras</li>
            </ul>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !url.trim() || !name.trim() || !usageContext.trim() || !responseTemplate.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={20} />
                Creando...
              </>
            ) : (
              <>
                <LinkIcon size={20} />
                Crear Recurso URL
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateURLResourceModal
